/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useState } from "react";

//@Packages
import { useTranslation } from "react-i18next";
import { Combobox, Control, Label, Input, ComboboxOption, PageLoader } from "@forge/common";

//@Components
import { DataCardTable } from "./dataCardTable";
import ConfirmationModal from "../../../reusablecomponents/confirmationmodal";

//@Models
import { Site } from "../../../models/devicelist";
import { DeviceDetails, OverviewTabProps } from "../../../models/viewdevice";

//@Utils
import { comboOptionValue } from "../../../utils/staticdata";
import { LOCALE_CONST, VIEW_DEVICE_OVERVIEW } from "../../../utils/constants";
import { extractNameplateValues, formatNameplateData } from "../../../utils/utility";

//@Styles
import "./styles.scss";

//@Functional component
const OverviewTab: React.FC<OverviewTabProps> = ({ data, isLoading, versionDetails, gatewayType }) => {
  const { t } = useTranslation();

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const nameplateValues = extractNameplateValues(versionDetails);
  //handle the configuration modal close
  const handleConfigurationClose = () => setIsConfirmationModalOpen(false);

  //render the name plate UI for basic info
  const renderNameplateUI = (gwType: string) => {
    const formattedData = formatNameplateData(nameplateValues[0], data, gwType, t);
    return <DataCardTable deviceData={formattedData} title={t(LOCALE_CONST.basicInfo)} />;
  };

  //Publish frequency options UI component
  const PublishFrequencyOptions = () => {
    return (
      <div className="publish-frequency-container">
        <Control className="w-[15rem] mt-2" spacingAbove spacingBelow>
          <Label
            className="font-normal iot-label-name-txt-cls"
            required
            requiredText=""
          >
            {VIEW_DEVICE_OVERVIEW.PUBLISH_FREQUENCY}
          </Label>
          <Input
            id={"publish-frequency-value"}
            className="frequency-input-box"
          />
        </Control>
        <Control className="w-[20rem] mt-2" spacingAbove spacingBelow>
          <Label className="font-normal iot-label-name-txt-cls"></Label>
          <Combobox
            debounce={300}
            height={10}
            i18nClear="Clear"
            i18nClearSelectedOptions="Clear selected options"
            i18nNoResults="No results"
            id="frequency-combobox"
            minCharacters={3}
            onBlur={function noRefCheck() { }}
            onChange={function noRefCheck() { }}
            onFocus={function noRefCheck() { }}
            placeholder={VIEW_DEVICE_OVERVIEW.SELECT_AN_OPTION}
            size="large"
            className="frequency-combobox"
          >
            {comboOptionValue.map((item) => (
              <ComboboxOption label={item.label} value={item.value} />
            ))}
          </Combobox>
        </Control>
      </div>
    );
  };
  
  const getSiteName = (siteRec: Site[]) =>
    siteRec?.map((item: Site) => item?.name)?.join(", ") || "-";

  const getAssociationData = (record: DeviceDetails) => {
    if (isLoading) {
      return [];
    } else {
      return [
        {
          label: t(LOCALE_CONST.organization),
          value: record?.organizationName || ""
        },
        { 
          type: "site",
          label: t(LOCALE_CONST.associatedSites), 
          value: getSiteName(record?.site || []) 
        }
      ];
    }
  };

  return (
    <>
      <PageLoader isLoading={isLoading} />
      {renderNameplateUI(gatewayType)}
      <DataCardTable deviceData={getAssociationData(data)} title={t(LOCALE_CONST.registrationDetails)} />
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={handleConfigurationClose}
        onConfirm={handleConfigurationClose}
        title={VIEW_DEVICE_OVERVIEW.CONFIGURATION}
        content={<PublishFrequencyOptions />}
        footerRightText={VIEW_DEVICE_OVERVIEW.SAVE}
      />
    </>
  );
};

export default OverviewTab;
